import React, {useEffect, useRef, useState, useCallback} from 'react';
import {ScrollView, LayoutChangeEvent, ActivityIndicator, View} from 'react-native';
import {RFValue} from 'react-native-responsive-fontsize';
import {
  PriceItem,
  Price,
  PriceProduct,
  OldPrice,
  OldPriceStrike,
  OldPriceWrapper,
} from './styles';
import {ProductDTO} from '../../../../dtos/ProductDTO';
import formatFuelData from '../../../../utils/formatFuelData';
import {formatFuelName} from '../../../../utils/formatFuelName';

function darkenColor(hex: string, amount: number = 0.1): string {
  const color = hex.replace('#', '');
  const r = Math.max(0, Math.round(parseInt(color.substring(0, 2), 16) * (1 - amount)));
  const g = Math.max(0, Math.round(parseInt(color.substring(2, 4), 16) * (1 - amount)));
  const b = Math.max(0, Math.round(parseInt(color.substring(4, 6), 16) * (1 - amount)));
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

interface AutoScrollPricesProps {
  products: ProductDTO[];
  onPress?: () => void;
  showPromotional?: boolean;
}

const SCROLL_STEP = 80; // pixels per step
const PAUSE_DURATION = 2000; // 2 seconds pause between steps

export function AutoScrollPrices({
  products,
  onPress,
  showPromotional = false,
}: AutoScrollPricesProps) {
  const scrollViewRef = useRef<ScrollView>(null);
  const scrollPositionRef = useRef(0);
  const directionRef = useRef<'forward' | 'backward'>('forward');
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isPausedRef = useRef(false);
  const maxScrollRef = useRef(0);
  const isRunningRef = useRef(false);

  const [contentWidth, setContentWidth] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);

  const maxScroll = Math.max(0, contentWidth - containerWidth);

  // Keep maxScrollRef updated
  useEffect(() => {
    maxScrollRef.current = maxScroll;
  }, [maxScroll]);

  const clearAutoScroll = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    isRunningRef.current = false;
  }, []);

  const scheduleNextStep = useCallback((delay: number) => {
    clearAutoScroll();

    timeoutRef.current = setTimeout(() => {
      if (isPausedRef.current || maxScrollRef.current <= 0) {
        return;
      }

      let reachedEdge = false;
      const maxScroll = maxScrollRef.current;

      // Calculate next position
      if (directionRef.current === 'forward') {
        const nextPosition = scrollPositionRef.current + SCROLL_STEP;
        // If the next step would leave us close to the end but not at the end,
        // jump directly to the end to avoid small awkward scrolls
        if (nextPosition >= maxScroll || (maxScroll - nextPosition) < SCROLL_STEP) {
          scrollPositionRef.current = maxScroll;
          directionRef.current = 'backward';
          reachedEdge = true;
        } else {
          scrollPositionRef.current = nextPosition;
        }
      } else {
        const nextPosition = scrollPositionRef.current - SCROLL_STEP;
        // If the next step would leave us close to the start but not at the start,
        // jump directly to the start to avoid small awkward scrolls
        if (nextPosition <= 0 || nextPosition < SCROLL_STEP) {
          scrollPositionRef.current = 0;
          directionRef.current = 'forward';
          reachedEdge = true;
        } else {
          scrollPositionRef.current = nextPosition;
        }
      }

      // Animate to position
      scrollViewRef.current?.scrollTo({
        x: scrollPositionRef.current,
        animated: true,
      });

      // Schedule next step after pause (double pause if reached edge)
      const nextDelay = reachedEdge ? PAUSE_DURATION * 2 : PAUSE_DURATION;
      scheduleNextStep(nextDelay);
    }, delay);

    isRunningRef.current = true;
  }, [clearAutoScroll]);

  const startAutoScroll = useCallback(() => {
    if (isRunningRef.current) {
      return;
    }

    clearAutoScroll();

    if (maxScrollRef.current <= 0) {
      return;
    }

    isPausedRef.current = false;
    // Start with initial pause
    scheduleNextStep(PAUSE_DURATION);
  }, [clearAutoScroll, scheduleNextStep]);

  const handleScrollBeginDrag = useCallback(() => {
    isPausedRef.current = true;
    clearAutoScroll();
  }, [clearAutoScroll]);

  const handleScrollEndDrag = useCallback(
    (event: any) => {
      // Update scroll position after user finishes dragging
      scrollPositionRef.current = event.nativeEvent.contentOffset.x;

      // Determine direction based on position
      if (scrollPositionRef.current >= maxScrollRef.current - 10) {
        directionRef.current = 'backward';
      } else if (scrollPositionRef.current <= 10) {
        directionRef.current = 'forward';
      }
    },
    [],
  );

  const handleMomentumScrollEnd = useCallback(
    (event: any) => {
      scrollPositionRef.current = event.nativeEvent.contentOffset.x;

      // Determine direction based on position
      if (scrollPositionRef.current >= maxScrollRef.current - 10) {
        directionRef.current = 'backward';
      } else if (scrollPositionRef.current <= 10) {
        directionRef.current = 'forward';
      }

      // Resume auto-scroll after 2 seconds of inactivity
      clearAutoScroll();
      timeoutRef.current = setTimeout(() => {
        isPausedRef.current = false;
        scheduleNextStep(PAUSE_DURATION);
      }, PAUSE_DURATION);
    },
    [clearAutoScroll, scheduleNextStep],
  );

  const handleContentSizeChange = useCallback(
    (width: number, _height: number) => {
      setContentWidth(width);
    },
    [],
  );

  const handleLayout = useCallback((event: LayoutChangeEvent) => {
    setContainerWidth(event.nativeEvent.layout.width);
  }, []);

  useEffect(() => {
    if (products.length > 0 && maxScroll > 0 && !isRunningRef.current) {
      startAutoScroll();
    }

    return () => {
      clearAutoScroll();
    };
  }, [products.length, maxScroll, startAutoScroll, clearAutoScroll]);

  if (!products || products.length === 0) {
    return (
      <View

      style={{
        marginRight: -12,
        marginLeft: -12,
        marginTop: RFValue(8),
        zIndex: 30,
        justifyContent:'center',
        alignItems: 'center',
      }}>
      <ActivityIndicator />
    </View>
    );
  }

  return (
    <ScrollView
      ref={scrollViewRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      scrollEnabled
      nestedScrollEnabled
      onScrollBeginDrag={handleScrollBeginDrag}
      onScrollEndDrag={handleScrollEndDrag}
      onMomentumScrollEnd={handleMomentumScrollEnd}
      onContentSizeChange={handleContentSizeChange}
      onLayout={handleLayout}

      style={{
        marginRight: -12,
        marginLeft: -12,
        marginTop: RFValue(6),
        zIndex: 30,
      }}>
      {products.map((item, index) => {
        const fuelColor = formatFuelData(item.name).bgColor;
        const currentPrice = item?.promotionalPrice || item.price;
        const hasPromotional = showPromotional && !!item?.promotionalPrice;
        return (
          <React.Fragment key={item.id}>
            <PriceItem
              activeOpacity={0.7}
              onPress={onPress}
             >
              <PriceProduct style={{color: darkenColor(fuelColor)}}>{formatFuelName(item.name)}</PriceProduct>
              {hasPromotional && (
                <OldPriceWrapper>
                  <OldPrice>{item.price.replace(' ', '')}</OldPrice>
                  <OldPriceStrike />
                </OldPriceWrapper>
              )}
              <Price>{currentPrice.replace(' ', '')}</Price>
            </PriceItem>
            {index < products.length - 1 && (
              <View style={{width: 1, backgroundColor: 'rgba(0,0,0,0.12)', marginHorizontal: RFValue(2), alignSelf: 'center', height: '55%'}} />
            )}
          </React.Fragment>
        );
      })}
    </ScrollView>
  );
}
